import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import _ from 'lodash';
import moment from 'moment';
import { compose } from 'recompose';
import { Image, Card, Icon, Popup, Table, Label } from 'semantic-ui-react';
import hasUserInfo from '../../hocs/has-user-info';

const tiktokIconStyle = {
  width: 20,
  margin: '2px 0.25rem 0 0',
};

const cardStyle = {
  color: 'black',
};

const thumbnailStyles = {
  height: '50px',
  display: 'block',
  margin: '0 auto',
  width: '50px',
};


const fadedStyle = {
  opacity: 0.4,
};

const assetCountH4Style = {
  color: '#fc4085',
};

const STATUS = {
  AUTHORIZED: 'AUTHORIZED',
  WARN: 'WARN',
};

const getDerivedColor = (status, instagramPrivate) => {
  if (status === STATUS.AUTHORIZED) return { style: null, color: 'green' };
  if ((status === STATUS.WARN) || instagramPrivate) return { style: null, color: 'orange' };
  return { style: fadedStyle, color: 'grey' };
};

const AuthorisationRow = ({
  platform,
  status,
}) => {
  const isAuthorised = status === STATUS.AUTHORIZED;
  const derivedColor = getDerivedColor(status);

  const isYouTube = (platform === 'youtube');
  const derivedPlatform = platform;
  return (
    <Popup
      content={`${platform} ${isAuthorised ? 'is' : 'is not'} authorised`} trigger={
        isYouTube ? <Label>YOUTUBE</Label>
          : <Icon
            size="large"
            name={derivedPlatform}
            style={derivedColor.style}
            color={derivedColor.color}
          />
      }
    />
  );
};

AuthorisationRow.propTypes = {
  platform: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
};


const INVITATION_STATUS = {
  deferred: 'DEFERRED',
  sent: 'SENT',
  accepted: 'ACCEPTED',
};

const getInvitationStatusIcon = (step, status) => {
  const isAccepted = status === INVITATION_STATUS.accepted;
  const isSent = status === INVITATION_STATUS.sent;

  if (step === 'invited' && (isAccepted || isSent)) {
    return <Icon name="checkmark" color="green" />;
  }

  if (step === 'joined' && status === INVITATION_STATUS.accepted) {
    return <Icon name="checkmark" color="green" />;
  }

  return <Icon name="close" color="gray" />;
};

const instagramPrivateWarning = {
  paddingBottom: '5px',
  paddingLeft: '5px',
};

const noPadding = {
  margin: 0, border: 0,
};

const scanWarningStyle = {
  paddingRight: 35,
  paddingBottom: 3,
  paddingTop: 3,
  borderTop: '1px solid #EEE',
  color: 'gray',
  textAlign: 'right',
};

const MAX_INSTA_SCAN_FAILS = 3;

const HorizontalCollaborationCard = ({
  discussionId,
  campaignId,
  influencerName,
  thumbnail,
  primarySocialHandle,
  email,
  isEmailValid,
  totalAssets,
  totalStories,
  invitationStatus,
  router,
  instagramStatus,
  facebookStatus,
  twitterStatus,
  youtubeStatus,
  instagramPrivate,
  lastScannedDate,
  lastSuccessfulStoryScan,
  isLiveCampaign,
  campaignWorkflow,
  internalDiscountCode,
  workflowStatus,
  userInfo,
  facebook,
  instagram,
  youtube,
  twitter,
  tiktok,
  telegramChannels,
  instagramScanFailures,
  hasPosted,
  isScanningCompleted,
  usageRightsGranted,
  rollfwdCampaign,
  reviewMode,
  discountCode,
}) => {
  const routerState = {
    influencer: { influencerName, thumbnail, primarySocialHandle, email },
    discussionId,
    isEmailValid,
    invitationStatus,
    rollfwdCampaign,
  };

  const now = moment();
  const lastScanHours = Math.round(moment.duration(now.diff(moment(lastScannedDate))).asHours());
  const lastStoryScanHours = Math.round(moment.duration(now.diff(moment(lastSuccessfulStoryScan))).asHours());
  const isScanWarning = lastScanHours > 20;
  const isStoryScanWarning = lastStoryScanHours > 10;

  const derivedColor = getDerivedColor(instagramStatus, instagramPrivate);
  const isWarn = (instagramStatus === STATUS.WARN) || instagramPrivate || isScanWarning;

  const isScanFailureError = instagramScanFailures > MAX_INSTA_SCAN_FAILS;

  if (reviewMode && isScanningCompleted) {
    return null;
  }
  return (
    <Card
      onClick={() => router.push({
        pathname: `/campaign/${campaignId}/collaboration/${discussionId}`,
        state: routerState,
      })}
      style={cardStyle}
      color={isWarn ? derivedColor.color : ''}
    >
      <Table padded={false} className="influencer-cards-table" style={noPadding}>
        <tbody style={{ backgroundColor: isScanFailureError ? 'white' : 'white' }}>
          <tr>
            <Table.Cell width={1} textAlign="center">
              <Image src={thumbnail} className="circular" style={thumbnailStyles} />
            </Table.Cell>
            <Table.Cell width={4}>
              <h4>{influencerName}</h4>
              <h5 style={{ color: 'gray' }}>{primarySocialHandle}</h5>
            </Table.Cell>
            <Table.Cell width={10} textAlign="center">
              <Table basic="very" textAlign="center">
                <Table.Row>
                  <Table.Cell><h5>invited</h5></Table.Cell>
                  <Table.Cell><h5>joined</h5></Table.Cell>
                  <Table.Cell><h5>platforms</h5></Table.Cell>
                  <Table.Cell><h5>posts</h5></Table.Cell>
                  <Table.Cell><h5>stories</h5></Table.Cell>
                  <Table.Cell><h5>status</h5></Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>
                    { getInvitationStatusIcon('invited', invitationStatus)}
                  </Table.Cell>
                  <Table.Cell>
                    { getInvitationStatusIcon('joined', invitationStatus)}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    { instagram && <AuthorisationRow platform="instagram" status={instagramStatus} /> }
                    { youtube && <AuthorisationRow platform="youtube" status={youtubeStatus} /> }
                    { facebook && <AuthorisationRow platform="facebook" status={facebookStatus} /> }
                    { twitter && <AuthorisationRow platform="twitter" status={twitterStatus} /> }
                    { tiktok && <img style={tiktokIconStyle} src="/logos/tiktok-logo-grey.svg" alt="Tiktok" /> }
                    { _.head(telegramChannels) && <Icon
                      size="large"
                      name="telegram"
                      style={fadedStyle}
                      color="grey"
                    /> }
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <h4 style={assetCountH4Style}>{totalAssets}</h4>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <h4 style={assetCountH4Style}>{totalStories}</h4>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {isScanningCompleted
                      ? <Popup
                        trigger={<Icon name="check circle outline" />}
                        content="This collaboration has been marked complete"
                      />
                      : <Popup
                        trigger={<Icon name="circle outline" color="green" />}
                        content="This collaboration is still scanning"
                      />
                    }
                    {
                      usageRightsGranted
                        ? <Popup
                          trigger={<Icon name="registered outline" color="pink" />}
                          content={`${influencerName} has granted usage rights to their content.`}
                        />
                        : <Popup
                          trigger={<Icon name="registered outline" color="grey" />}
                          content={`${influencerName} has NOT granted usage rights to their content.`}
                        />
                    }
                  </Table.Cell>
                </Table.Row>
              </Table>
            </Table.Cell>
          </tr>
        </tbody>
      </Table>
      { instagramPrivate &&
        <div style={instagramPrivateWarning}>
          <Label color="orange" basic>
            &nbsp;Instagram account is private&nbsp;
            <Icon name="lock" />- The system cannot scan it.
          </Label>
        </div>
      }

      { isLiveCampaign && userInfo.isDyzioUser && !isScanningCompleted &&
        <div style={scanWarningStyle}>
          <Label basic color={isScanWarning ? 'red' : 'grey'}>last GRID scan:&nbsp;
            { isScanWarning && <Icon color="red" name="warning circle" />}
            { lastScanHours } hours ago
          </Label>

          { instagram &&
            <Label basic color={isStoryScanWarning ? 'red' : 'grey'}>last STORY scan:&nbsp;
              { isStoryScanWarning && <Icon color="red" name="warning circle" />}
              { lastStoryScanHours } hours ago
            </Label>
          }
        </div>
      }

      { campaignWorkflow &&
        <div style={scanWarningStyle}>

          {discountCode && <Label basic color="blue" style={{ width: '200px' }}><Icon name="code" />{_.toUpper(discountCode)}</Label>}
          <Label basic color="pink" style={{ width: '250px' }}><Icon name="check circle outline" />{workflowStatus}</Label>
        </div>

      }

    </Card>
  );
};

HorizontalCollaborationCard.propTypes = {
  discussionId: PropTypes.string.isRequired,
  instagramStatus: PropTypes.string.isRequired,
  facebookStatus: PropTypes.string.isRequired,
  twitterStatus: PropTypes.string.isRequired,
  youtubeStatus: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  influencerName: PropTypes.string.isRequired,
  totalAssets: PropTypes.number.isRequired,
  totalStories: PropTypes.number.isRequired,
  thumbnail: PropTypes.string.isRequired,
  primarySocialHandle: PropTypes.string.isRequired,
  invitationStatus: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  isEmailValid: PropTypes.bool.isRequired,
  isLiveCampaign: PropTypes.bool.isRequired,
  instagramPrivate: PropTypes.bool.isRequired,
  router: PropTypes.object.isRequired,
  lastScannedDate: PropTypes.string.isRequired,
  lastSuccessfulStoryScan: PropTypes.string.isRequired,
  userInfo: PropTypes.object.isRequired,
};

export default compose(
  withRouter,
  hasUserInfo,
)(HorizontalCollaborationCard);



/** WEBPACK FOOTER **
 ** ./src/components/horizontal-collaboration-card/index.js
 **/